import React, { useEffect, useMemo, useState } from "react";
import { Card } from "primereact/card";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { FilterMatchMode } from "primereact/api";

import { Tag } from "primereact/tag";
import { CommunityPriceRow } from "../types";
import { SearchHeader } from "../../QrCodeManagement/components/SearchHeader";
import { useNavigate } from "react-router-dom";

export interface CommunityPricesTableProps {
  rows: CommunityPriceRow[];
  search: string;
  setSearch: (v: string) => void;
  initialLoading: boolean;
  refreshing: boolean;
  toastRef: React.RefObject<Toast>;
}

const DayNightValue: React.FC<{ day?: string; night?: string }> = ({
  day,
  night,
}) => (
  <span className="flex align-items-center gap-2">
    <span className="flex align-items-center gap-1">
      <i className="pi pi-sun" style={{ color: "#f59e0b" }} />
      {day ?? "—"}
    </span>
    <span className="text-400">|</span>
    <span className="flex align-items-center gap-1">
      <i className="pi pi-moon" style={{ color: "#6366f1" }} />
      {night ?? "—"}
    </span>
  </span>
);

const TARIFF_TYPE_LABEL: Record<string, string> = {
  REGULAR: "STATIC",
  DAY_NIGHT: "DAY-NIGHT",
  HOURLY: "HOURLY",
};

type TagSeverity = React.ComponentProps<typeof Tag>["severity"];

const TARIFF_TYPE_SEVERITY: Record<string, TagSeverity> = {
  REGULAR: undefined,
  DAY_NIGHT: "warning",
  HOURLY: "info",
};

const tariffTypeBody = (row: CommunityPriceRow) => {
  const label = TARIFF_TYPE_LABEL[row.tariff_type] ?? row.tariff_type;
  const severity: TagSeverity = TARIFF_TYPE_SEVERITY[row.tariff_type] ?? "info";
  return <Tag value={label} severity={severity} style={{ color: "#fff" }} />;
};

const priceBody =
  (
    field: keyof CommunityPriceRow,
    dayField: keyof CommunityPriceRow,
    nightField: keyof CommunityPriceRow,
  ) =>
  (row: CommunityPriceRow) => {
    if (row.tariff_type === "DAY_NIGHT") {
      return (
        <DayNightValue
          day={row[dayField] as string}
          night={row[nightField] as string}
        />
      );
    }
    return <span>{row[field] as string}</span>;
  };

export const CommunityPricesTable: React.FC<CommunityPricesTableProps> = ({
  rows,
  search,
  setSearch,
  initialLoading,
  refreshing,
  toastRef,
}) => {
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    location_id: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const navigate = useNavigate();

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      location_id: { value: search, matchMode: FilterMatchMode.CONTAINS },
    }));
  }, [search]);

  const header = useMemo(
    () => (
      <SearchHeader
        value={search}
        onChange={setSearch}
        placeholder="Search by Station ID"
      />
    ),
    [search, setSearch],
  );

  if (initialLoading) {
    return (
      <div className="loading-container">
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <>
      <Toast
        ref={toastRef}
        position="top-right"
        appendTo={typeof window !== "undefined" ? document.body : undefined}
      />
      <Card
        className="p-4"
        title={
          <div className="flex flex-column md:flex-row justify-content-between align-items-center">
            <div className="text-2xl font-semibold text-900">
              Community Prices
            </div>
          </div>
        }
      >
        <DataTable
          value={rows}
          paginator
          rows={10}
          dataKey="location_id"
          filters={filters}
          emptyMessage="No community prices found."
          header={header}
          loading={refreshing}
          className="p-datatable-gridlines cursor-pointer"
          sortMode="single"
          rowHover
          onRowClick={(e: any) =>
            navigate(`/community-prices/history/${e?.data?.location_id}`)
          }
        >
          <Column
            field="tariff_type"
            header="Tariff Type"
            body={tariffTypeBody}
          />
          <Column field="location_id" header="Station ID" sortable />
          <Column field="tariff_id" header="Tariff ID" />
          <Column
            field="energy_price"
            header="Energy Price"
            sortable
            body={priceBody(
              "energy_price",
              "energy_price_day",
              "energy_price_night",
            )}
          />
          <Column
            field="blocking_fee"
            header="Blocking Fee"
            sortable
            body={priceBody(
              "blocking_fee",
              "blocking_fee_day",
              "blocking_fee_night",
            )}
          />
          <Column
            field="blocking_fee_after_hours"
            header="Blocking Fee After hours"
            sortable
            body={priceBody(
              "blocking_fee_after_hours",
              "blocking_fee_after_hours_day",
              "blocking_fee_after_hours_night",
            )}
          />
          <Column
            field="blocking_fee_cap"
            header="Blocking Fee Cap"
            sortable
            body={priceBody(
              "blocking_fee_cap",
              "blocking_fee_cap_day",
              "blocking_fee_cap_night",
            )}
          />
          <Column
            field="blocking_fee_cap_after_hours"
            header="Blocking Fee Cap After hours"
            sortable
            body={priceBody(
              "blocking_fee_cap_after_hours",
              "blocking_fee_cap_after_hours_day",
              "blocking_fee_cap_after_hours_night",
            )}
          />
        </DataTable>
      </Card>
    </>
  );
};
